import { ChangeDetectionStrategy, Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '@core/auth';
import { ConfigService, LoggerService } from '@core/services';
import { AppSettings, Settings } from 'app/app.settings';
import { ConfigSettings } from 'environments/ConfigSettings';
import { ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { MatSidenavModule } from '@angular/material/sidenav';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'app-admin-register',
  templateUrl: './admin-register.component.html',
  styleUrls: ['./admin-register.component.scss'],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatSidenavModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminRegisterComponent implements OnInit {
  formGroup: FormGroup = new FormGroup(
    {
      username: new FormControl('', [Validators.required, Validators.email, Validators.maxLength(250)]),
      firstname: new FormControl('', [Validators.required, Validators.maxLength(250)]),
      lastname: new FormControl('', [Validators.required, Validators.maxLength(250)]),
      password: new FormControl('', [Validators.required, Validators.minLength(8), Validators.maxLength(250)]),
      confirmPassword: new FormControl('', [Validators.required]),
    },
    { validators: this.passwordMatchValidator }
  );

  loading = false;
  config!: ConfigSettings;
  settings: Settings;

  constructor(
    public appSettings: AppSettings,
    private authService: AuthService,
    private router: Router,
    private logger: LoggerService,
    private configService: ConfigService,
    private destroyRef: DestroyRef
  ) {
    this.settings = this.appSettings.settings;
  }

  ngOnInit(): void {
    this.configService.Config.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((config) => {
      if (!config) return;
      this.config = config;
    });

    // Verify this is still a new instance before showing the form
    this.verifyNewInstance();
  }

  public get getLogo(): string {
    return this.config?.logo;
  }

  public get getClient(): string {
    if (this.config?.environmentName?.includes('local')) {
      return `${this.config?.client} - ${this.config.environmentName}`;
    }
    return this.config?.client;
  }

  private passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    const password = control.get('password')?.value;
    const confirmPassword = control.get('confirmPassword')?.value;
    return password === confirmPassword ? null : { passwordMismatch: true };
  }

  private async verifyNewInstance(): Promise<void> {
    try {
      const response = await lastValueFrom(this.authService.checkIsNewInstance());
      if (!response?.successful || !response.data?.isNewInstance) {
        // Users exist, redirect to login
        this.router.navigate(['/login']);
      }
    } catch {
      this.logger.displayError('Error verifying system state', 'Admin Register');
      this.router.navigate(['/login']);
    }
  }

  async register(): Promise<void> {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    this.loading = true;
    this.formGroup.disable();

    const formValue = this.formGroup.value;

    try {
      const result = await lastValueFrom(
        this.authService.createFirstAdmin({
          username: formValue.username,
          firstname: formValue.firstname,
          lastname: formValue.lastname,
          password: formValue.password,
        })
      );

      if (result) {
        this.logger.displaySuccess('Admin account created successfully', 'Admin Register');
        // Auto-login the newly created admin user
        const loginResult = await this.authService.login(formValue.username, formValue.password);
        if (loginResult?.successful) {
          this.router.navigate(['/']);
        } else {
          this.router.navigate(['/login']);
        }
      }
    } catch (error: unknown) {
      const errorMessage =
        (error as { error?: { message?: string } })?.error?.message || 'Failed to create admin account';
      this.logger.displayError(errorMessage, 'Admin Register');
    } finally {
      this.loading = false;
      this.formGroup.enable();
    }
  }

  get usernameError(): string {
    const control = this.formGroup.get('username');
    if (control?.hasError('required')) return 'Username is required';
    if (control?.hasError('email')) return 'Username must be a valid email';
    if (control?.hasError('maxlength')) return 'Username cannot be more than 250 characters';
    return '';
  }

  get firstnameError(): string {
    const control = this.formGroup.get('firstname');
    if (control?.hasError('required')) return 'First name is required';
    if (control?.hasError('maxlength')) return 'First name cannot be more than 250 characters';
    return '';
  }

  get lastnameError(): string {
    const control = this.formGroup.get('lastname');
    if (control?.hasError('required')) return 'Last name is required';
    if (control?.hasError('maxlength')) return 'Last name cannot be more than 250 characters';
    return '';
  }

  get passwordError(): string {
    const control = this.formGroup.get('password');
    if (control?.hasError('required')) return 'Password is required';
    if (control?.hasError('minlength')) return 'Password must be at least 8 characters';
    if (control?.hasError('maxlength')) return 'Password cannot be more than 250 characters';
    return '';
  }

  get confirmPasswordError(): string {
    const control = this.formGroup.get('confirmPassword');
    if (control?.hasError('required')) return 'Please confirm your password';
    if (this.formGroup.hasError('passwordMismatch')) return 'Passwords do not match';
    return '';
  }

  get usernameHasError(): boolean {
    return (this.formGroup.dirty || this.formGroup.touched) && (this.formGroup.get('username')?.invalid ?? false);
  }

  get firstnameHasError(): boolean {
    return (this.formGroup.dirty || this.formGroup.touched) && (this.formGroup.get('firstname')?.invalid ?? false);
  }

  get lastnameHasError(): boolean {
    return (this.formGroup.dirty || this.formGroup.touched) && (this.formGroup.get('lastname')?.invalid ?? false);
  }

  get passwordHasError(): boolean {
    return (this.formGroup.dirty || this.formGroup.touched) && (this.formGroup.get('password')?.invalid ?? false);
  }

  get confirmPasswordHasError(): boolean {
    return (
      (this.formGroup.dirty || this.formGroup.touched) && (this.formGroup.get('confirmPassword')?.invalid ?? false)
    );
  }
}
